import axios from "axios";
import { baseURL } from "./index";

// Create a custom Axios instance
const apiClient = axios.create({
  // baseURL: "http://localhost:8080/", // Replace with your desired base URL
  baseURL: baseURL, // Replace with your desired base URL
  timeout: 30000, // Optional: set a timeout for requests
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add Request Interceptor to attach access token
apiClient.interceptors.request.use(
  (config) => {
    const authData = localStorage.getItem("asg_phedr_admin");
    if (authData) {
      try {
        const auth = JSON.parse(authData);
        if (auth?.accessToken) {
          config.headers.Authorization = `Bearer ${auth.accessToken}`;
        }
      } catch (error) {
        console.error("Error parsing auth data:", error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add Response Interceptor with token refresh logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const authData = localStorage.getItem("asg_phedr_admin");
      if (!authData) {
        processQueue(error, null);
        isRefreshing = false;
        localStorage.removeItem("asg_phedr_admin");
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const auth = JSON.parse(authData);
        const refreshToken = auth?.refreshToken;

        if (!refreshToken) {
          processQueue(error, null);
          isRefreshing = false;
          localStorage.removeItem("asg_phedr_admin");
          window.location.href = "/login";
          return Promise.reject(error);
        }

        // Call refresh token API
        const response = await axios.post(
          `${baseURL}api/v1/auth/refresh`,
          { refreshToken: refreshToken }
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        // Update tokens in localStorage
        const updatedAuth = {
          ...auth,
          accessToken: accessToken,
          refreshToken: newRefreshToken || refreshToken,
        };
        localStorage.setItem("asg_phedr_admin", JSON.stringify(updatedAuth));

        // Update the original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        // Process queued requests
        processQueue(null, accessToken);
        isRefreshing = false;

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh token failed, logout user
        processQueue(refreshError, null);
        isRefreshing = false;
        localStorage.removeItem("asg_phedr_admin");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
